import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import DoctorCard from './DoctorCard';
import Pagination from './Pagination';
import Breadcrumbs from './Breadcrumbs';
import SEO from './SEO';
import { getPlaces } from '../services/api';

const PlaceList = ({ specialty = 'ginecologos', isCDMX = false }) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Extraer los slugs de la URL primero
  const pathParts = location.pathname.split('/');
  const stateSlug = pathParts[1];
  const citySlug = pathParts[2]?.includes('-en-') ? pathParts[2].split('-en-')[1] : pathParts[2];
  const specialtyFromPath = pathParts[2]?.split('-en-')[0];
  
  // Determinar la especialidad actual
  const currentSpecialty = searchParams.get('specialty') || specialtyFromPath || specialty;
  
  // Obtener los datos iniciales del SSR solo si coincide la especialidad y la ruta
  const ssrContent = document.getElementById('ssr-content');
  const initialPlaces = ssrContent ? Array.from(ssrContent.querySelectorAll('.doctor-card')).map(card => {
    const mainCategory = card.querySelector('[data-category]')?.dataset.category;
    const expectedCategory = currentSpecialty === 'urologos' ? 'Urologo' : 
                           currentSpecialty === 'pediatras' ? 'Pediatra' : 
                           currentSpecialty === 'dermatologos' ? 'Dermatologo' : 
                           currentSpecialty === 'traumatologos' ? 'Cirujano ortopedico' :
                           currentSpecialty === 'dentistas' ? 'Dentista' :
                           currentSpecialty === 'otorrinolaringologos' ? 'Otorrinolaringologo' :
                           currentSpecialty === 'nutriologos' ? 'Nutricionista' :
                           currentSpecialty === 'oftalmologos' ? 'Oftalmólogo' :
                           currentSpecialty === 'psiquiatras' ? 'Psiquiatra' :
                           currentSpecialty === 'cardiologos' ? 'Cardiologo' :
                           currentSpecialty === 'endocrinologos' ? 'Endocrinologo' :
                           currentSpecialty === 'gastroenterologos' ? 'Gastroenterologo' :
                           currentSpecialty === 'cirujanos-plasticos' ? 'Cirujano Plastico' :
                           currentSpecialty === 'psicologos' ? 'Psicologo' :
                           currentSpecialty === 'podologos' ? 'Podologo' :
                           currentSpecialty === 'acupuntores' ? 'Acupuntor' :
                           currentSpecialty === 'reumatologos' ? 'Reumatologo' :
                           currentSpecialty === 'internistas' ? 'Internista' :
                           currentSpecialty === 'geriatras' ? 'Geriatra' :
                           currentSpecialty === 'angiologos' ? 'Angiologo' :
                           currentSpecialty === 'neurologos' ? 'Neurologo' :
                           currentSpecialty === 'homeopatas' ? 'Homeopata' :
                           currentSpecialty === 'alergologos' ? 'Alergologo' :
                           currentSpecialty === 'fisioterapeutas' ? 'Fisioterapeuta' :
                           currentSpecialty === 'laboratorios-de-analisis-clinicos' ? 'Laboratorio de Análisis Clínicos' :
                           currentSpecialty === 'nefrologos' ? 'Nefrologo' :
                           currentSpecialty === 'proctologos' ? 'Proctologo' :
                           currentSpecialty === 'neumologos' ? 'Neumologo' :
                           currentSpecialty === 'medicos-familiares' ? 'Medico Familiar' :
                           currentSpecialty === 'spa-terapeuticos' ? 'Spa Terapéutico' :
                           currentSpecialty === 'farmacias' ? 'Farmacia' :
                           currentSpecialty === 'audiologos' ? 'Audiologo' :
                           currentSpecialty === 'masajistas' ? 'Masajista' :
                           currentSpecialty === 'tiendas-de-articulos-ortopedicos' ? 'Tienda de Articulos Ortopédicos' :
                           currentSpecialty === 'tiendas-de-sillas-de-ruedas' ? 'Tienda de Sillas de Ruedas' :
                           currentSpecialty === 'opticas' ? 'Optica' :
                           currentSpecialty === 'radiologos' ? 'Radiologo' :
                           currentSpecialty === 'optometristas' ? 'Optometrista' :
                           currentSpecialty === 'quiropractico' ? 'Quiropractico' :
                           currentSpecialty === 'periodoncistas' ? 'Periodoncista' :
                           currentSpecialty === 'esteticistas' ? 'Esteticista' :
                           'Ginecologo';
    
    // Solo incluir el doctor si la categoría coincide
    if (mainCategory === expectedCategory) {
      return {
        name: card.querySelector('h2').textContent,
        address: card.querySelector('p').textContent,
        phone: card.querySelector('[data-phone]')?.dataset.phone,
        contact: {
          website: card.querySelector('[data-website]')?.dataset.website
        },
        location: {
          address: card.querySelector('p').textContent,
          coordinates: {
            lat: parseFloat(card.querySelector('[data-lat]')?.dataset.lat),
            lng: parseFloat(card.querySelector('[data-lng]')?.dataset.lng)
          },
          mapUrl: card.querySelector('[data-map-url]')?.dataset.mapUrl
        },
        schedule: {
          workdays: card.querySelector('[data-workdays]')?.dataset.workdays
        },
        rating: card.querySelector('.rating') ? {
          average: parseFloat(card.querySelector('.rating meta[itemprop="ratingValue"]').content),
          total_reviews: parseInt(card.querySelector('.rating meta[itemprop="reviewCount"]').content)
        } : null,
        websites: card.querySelector('[data-websites]') ? 
          JSON.parse(card.querySelector('[data-websites]').dataset.websites) : [],
        main_category: mainCategory
      };
    }
    return null;
  }).filter(Boolean) : [];

  const [places, setPlaces] = useState(initialPlaces); // Asegúrate de que initialPlaces tenga los datos correctos
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [pagination, setPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [cityInfo, setCityInfo] = useState(null);
  
  // Determinar si es una ruta de CDMX
  const isCDMXRoute = stateSlug === 'ciudad-de-mexico' && 
    (citySlug === 'ciudad-de-mexico' || 
     pathParts[2] === 'ginecologos-en-ciudad-de-mexico' || 
     pathParts[2] === 'urologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'pediatras-en-ciudad-de-mexico' || 
     pathParts[2] === 'dermatologos-en-ciudad-de-mexico' || 
     pathParts[2] === 'traumatologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'dentistas-en-ciudad-de-mexico' ||
     pathParts[2] === 'otorrinolaringologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'nutriologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'oftalmologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'psiquiatras-en-ciudad-de-mexico' ||
     pathParts[2] === 'cardiologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'endocrinologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'gastroenterologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'cirujanos-plasticos-en-ciudad-de-mexico' ||
     pathParts[2] === 'podologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'psicologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'reumatologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'internistas-en-ciudad-de-mexico' ||
     pathParts[2] === 'angiologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'neurologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'homeopatas-en-ciudad-de-mexico' ||
     pathParts[2] === 'alergologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'fisioterapeutas-en-ciudad-de-mexico' ||
     pathParts[2] === 'laboratorios-de-analisis-clinicos-en-ciudad-de-mexico' ||
     pathParts[2] === 'geriatras-en-ciudad-de-mexico' ||
     pathParts[2] === 'nefrologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'proctologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'neumologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'medicos-familiares-en-ciudad-de-mexico' ||
     pathParts[2] === 'spa-terapeuticos-en-ciudad-de-mexico' ||
     pathParts[2] === 'farmacias-en-ciudad-de-mexico' ||
     pathParts[2] === 'audiologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'masajistas-en-ciudad-de-mexico' ||
     pathParts[2] === 'tiendas-de-articulos-ortopedicos-en-ciudad-de-mexico' ||
     pathParts[2] === 'tiendas-de-sillas-de-ruedas-en-ciudad-de-mexico' ||
     pathParts[2] === 'opticas-en-ciudad-de-mexico' ||
     pathParts[2] === 'radiologos-en-ciudad-de-mexico' ||
     pathParts[2] === 'optometristas-en-ciudad-de-mexico' ||
     pathParts[2] === 'quiropractico-en-ciudad-de-mexico' ||
     pathParts[2] === 'periodoncistas-en-ciudad-de-mexico' ||
     pathParts[2] === 'esteticistas-en-ciudad-de-mexico');

  // Si es una ruta de CDMX, usar el slug especial
  const effectiveCitySlug = isCDMXRoute ? 
    `${specialty}-en-ciudad-de-mexico` : 
    `${currentSpecialty}-en-${citySlug}`;

  const page = parseInt(searchParams.get('page')) || 1;

  // Mantener el debounce para la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Esperar un momento antes de hacer la petición
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const response = await getPlaces({
          stateSlug,
          citySlug: effectiveCitySlug,
          page,
          search: debouncedSearch,
          specialty: currentSpecialty
        });
        
        console.log('API Response:', response);
        
        if (!response.places) {
          throw new Error('No se recibieron datos válidos del servidor');
        }

        setPlaces(response.places);
        setCityInfo(response.cityInfo);
        setPagination(response.pagination);
        setError(null);
      } catch (error) {
        console.error('Error fetching places:', error);
        setError('Error al cargar los lugares');
        
        // Reintentar hasta 3 veces si hay error
        if (retryCount < 3) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 1000);
        }
      } finally {
        setLoading(false);
      }
    };

    if (stateSlug && effectiveCitySlug) {
      fetchPlaces();
    }
  }, [stateSlug, effectiveCitySlug, page, debouncedSearch, currentSpecialty, retryCount]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setSearchParams({ page: '1' });
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  const getBreadcrumbItems = () => {
    const items = [
      {
        name: 'Especialidades',
        href: '/especialidades-medicas'
      },
      {
        name: currentSpecialty === 'urologos' ? 'Urólogos' : 
             currentSpecialty === 'pediatras' ? 'Pediatras' : 
             currentSpecialty === 'dermatologos' ? 'Dermatólogos' : 
             currentSpecialty === 'traumatologos' ? 'Traumatólogos' :
             currentSpecialty === 'dentistas' ? 'Dentistas' :
             currentSpecialty === 'otorrinolaringologos' ? 'Otorrinolaringólogos' :
             currentSpecialty === 'nutriologos' ? 'Nutriólogos' :
             currentSpecialty === 'oftalmologos' ? 'Oftalmólogos' :
             currentSpecialty === 'psiquiatras' ? 'Psiquiatras' :
             currentSpecialty === 'cardiologos' ? 'Cardiólogos' :
             currentSpecialty === 'endocrinologos' ? 'Endocrinólogos' :
             currentSpecialty === 'gastroenterologos' ? 'Gastroenterólogos' :
             currentSpecialty === 'cirujanos-plasticos' ? 'Cirujanos Plásticos' :
             currentSpecialty === 'psicologos' ? 'Psicólogos' :
             currentSpecialty === 'podologos' ? 'Podólogos' :
             currentSpecialty === 'acupuntores' ? 'Acupuntores' :
             currentSpecialty === 'clinica de acupuntura' ? 'Acupuntores' :
             currentSpecialty === 'reumatologos' ? 'Reumatologo' :
             currentSpecialty === 'internistas' ? 'Internistas' :
             currentSpecialty === 'geriatras' ? 'Geriatras' :
             currentSpecialty === 'angiologos' ? 'Angiologos' :
             currentSpecialty === 'neurologos' ? 'Neurologos' :
             currentSpecialty === 'homeopatas' ? 'Homeopatas' :
             currentSpecialty === 'alergologos' ? 'Alergologos' :
             currentSpecialty === 'fisioterapeutas' ? 'Fisioterapeutas' :
             currentSpecialty === 'laboratorios-de-analisis-clinicos' ? 'Laboratorios de Análisis Clínicos' :
             currentSpecialty === 'nefrologos' ? 'Nefrologos' :
             currentSpecialty === 'proctologos' ? 'Proctologos' :
             currentSpecialty === 'neumologos' ? 'Neumologos' :
             currentSpecialty === 'medicos-familiares' ? 'Medicos Familiares' :
             currentSpecialty === 'spa-terapeuticos' ? 'Spa Terapéuticos' :
             currentSpecialty === 'farmacias' ? 'Farmacias' :
             currentSpecialty === 'audiologos' ? 'Audiologos' :
             currentSpecialty === 'masajistas' ? 'Masajistas' :
             currentSpecialty === 'tiendas-de-articulos-ortopedicos' ? 'Tiendas de Articulos Ortopédicos' :
             currentSpecialty === 'tiendas-de-sillas-de-ruedas' ? 'Tiendas de Sillas de Ruedas' :
             currentSpecialty === 'opticas' ? 'Opticas' :
             currentSpecialty === 'radiologos' ? 'Radiologos' :
             currentSpecialty === 'optometristas' ? 'Optometristas' :
             currentSpecialty === 'quiropractico' ? 'Quiropracticos' :
             currentSpecialty === 'periodoncistas' ? 'Periodoncistas' :
             currentSpecialty === 'esteticistas' ? 'Esteticistas' :
             'Ginecólogos',
        href: `/especialidades-medicas/en-detalle/${specialty}`
      }
    ];

    if (cityInfo) {
      items.push({
        name: isCDMX ? 'Ciudad de México' : cityInfo.cityName,
        href: `/${stateSlug}/${specialty}-en-${effectiveCitySlug}`
      });
    }

    return items;
  };

  const isSSRPage = location.pathname.startsWith('/estado/');

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse text-center">
          <div className="text-lg">Cargando resultados...</div>
          <div className="text-sm text-gray-500">Por favor espere</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div id="ssr-content" />
      <SEO 
        title={`${currentSpecialty === 'urologos' ? 'Urólogos' : 
               currentSpecialty === 'pediatras' ? 'Pediatras' : 
               currentSpecialty === 'dermatologos' ? 'Dermatólogos' : 
               currentSpecialty === 'traumatologos' ? 'Traumatólogos' :
               currentSpecialty === 'dentistas' ? 'Dentistas' :
               currentSpecialty === 'otorrinolaringologos' ? 'Otorrinolaringólogos' :
               currentSpecialty === 'nutriologos' ? 'Nutriólogos' :
               currentSpecialty === 'oftalmologos' ? 'Oftalmólogos' :
               currentSpecialty === 'psiquiatras' ? 'Psiquiatras' :
               currentSpecialty === 'cardiologos' ? 'Cardiólogos' :
               currentSpecialty === 'endocrinologos' ? 'Endocrinólogos' :
               currentSpecialty === 'gastroenterologos' ? 'Gastroenterólogos' :
               currentSpecialty === 'cirujanos-plasticos' ? 'Cirujanos Plásticos' :
               currentSpecialty === 'psicologos' ? 'Psicólogos' :
               currentSpecialty === 'podologos' ? 'Podólogos' :
               currentSpecialty === 'acupuntores' ? 'Acupuntores' :
               currentSpecialty === 'clinica de acupuntura' ? 'Acupuntores' :
               currentSpecialty === 'reumatologos' ? 'Reumatologo' :
               currentSpecialty === 'internistas' ? 'Internistas' :
               currentSpecialty === 'geriatras' ? 'Geriatras' :
               currentSpecialty === 'angiologos' ? 'Angiologos' :
               currentSpecialty === 'neurologos' ? 'Neurologos' :
               currentSpecialty === 'homeopatas' ? 'Homeopatas' :
               currentSpecialty === 'alergologos' ? 'Alergologos' :
               currentSpecialty === 'fisioterapeutas' ? 'Fisioterapeutas' :
               currentSpecialty === 'laboratorios-de-analisis-clinicos' ? 'Laboratorios de Análisis Clínicos' :
               currentSpecialty === 'nefrologos' ? 'Nefrologos' :
               currentSpecialty === 'proctologos' ? 'Proctologos' :
               currentSpecialty === 'neumologos' ? 'Neumologos' :
               currentSpecialty === 'medicos-familiares' ? 'Medicos Familiares' :
               currentSpecialty === 'spa-terapeuticos' ? 'Spa Terapéuticos' :
               currentSpecialty === 'farmacias' ? 'Farmacias' :
               currentSpecialty === 'audiologos' ? 'Audiologos' :
               currentSpecialty === 'masajistas' ? 'Masajistas' :
               currentSpecialty === 'tiendas-de-articulos-ortopedicos' ? 'Tiendas de Articulos Ortopédicos' :
               currentSpecialty === 'tiendas-de-sillas-de-ruedas' ? 'Tiendas de Sillas de Ruedas' :
               currentSpecialty === 'opticas' ? 'Opticas' :
               currentSpecialty === 'radiologos' ? 'Radiologos' :
               currentSpecialty === 'optometristas' ? 'Optometristas' :
               currentSpecialty === 'quiropractico' ? 'Quiropracticos' :
               currentSpecialty === 'periodoncistas' ? 'Periodoncistas' :
               currentSpecialty === 'esteticistas' ? 'Esteticistas' :
               'Ginecólogos'} en ${isCDMX ? 'Ciudad de México' : cityInfo?.cityName || ''} | Directorio Médico`}
        description={`Encuentra los mejores ${currentSpecialty === 'urologos' ? 'urólogos' : 
                    currentSpecialty === 'pediatras' ? 'pediatras' : 
                    currentSpecialty === 'dermatologos' ? 'dermatólogos' : 
                    currentSpecialty === 'traumatologos' ? 'traumatólogos' :
                    currentSpecialty === 'dentistas' ? 'dentistas' :
                    currentSpecialty === 'otorrinolaringologos' ? 'otorrinolaringólogos' :
                    currentSpecialty === 'nutriologos' ? 'nutriólogos' :
                    currentSpecialty === 'oftalmologos' ? 'oftalmólogos' :
                    currentSpecialty === 'psiquiatras' ? 'psiquiatras' :
                    currentSpecialty === 'cardiologos' ? 'cardiólogos' :
                    currentSpecialty === 'endocrinologos' ? 'endocrinólogos' :
                    currentSpecialty === 'gastroenterologos' ? 'gastroenterólogos' :
                    currentSpecialty === 'cirujanos-plasticos' ? 'cirujanos plásticos' :
                    currentSpecialty === 'psicologos' ? 'psicólogos' :
                    currentSpecialty === 'podologos' ? 'podólogos' :
                    currentSpecialty === 'acupuntores' || currentSpecialty === 'Clinica de Acupuntura' ? 'Acupuntores' :
                    currentSpecialty === 'reumatologos' ? 'Reumatologos' :
                    currentSpecialty === 'internistas' ? 'Internistas' :
                    currentSpecialty === 'geriatras' ? 'geriatras' :
                    currentSpecialty === 'angiologos' ? 'angiologos' :
                    currentSpecialty === 'neurologos' ? 'neurologos' :
                    currentSpecialty === 'homeopatas' ? 'homeopatas' :
                    currentSpecialty === 'alergologos' ? 'alergologos' :
                    currentSpecialty === 'fisioterapeutas' ? 'fisioterapeutas' :
                    currentSpecialty === 'laboratorios-de-analisis-clinicos' ? 'laboratorios-de-analisis-clinicos' :
                    currentSpecialty === 'nefrologos' ? 'nefrologos' :
                    currentSpecialty === 'proctologos' ? 'proctologos' :
                    currentSpecialty === 'neumologos' ? 'neumologos' :
                    currentSpecialty === 'medicos-familiares' ? 'medicos-familiares' :
                    currentSpecialty === 'spa-terapeuticos' ? 'spa-terapeuticos' :
                    currentSpecialty === 'farmacias' ? 'farmacias' :
                    currentSpecialty === 'audiologos' ? 'audiologos' :
                    currentSpecialty === 'masajistas' ? 'masajistas' :
                    currentSpecialty === 'tiendas-de-articulos-ortopedicos' ? 'tiendas-de-articulos-ortopedicos' :
                    currentSpecialty === 'tiendas-de-sillas-de-ruedas' ? 'tiendas-de-sillas-de-ruedas' :
                    currentSpecialty === 'opticas' ? 'opticas' :
                    currentSpecialty === 'radiologos' ? 'radiologos' :
                    currentSpecialty === 'optometristas' ? 'optometristas' :
                    currentSpecialty === 'quiropractico' ? 'quiropracticos' :
                    currentSpecialty === 'periodoncistas' ? 'periodoncistas' :
                    currentSpecialty === 'esteticistas' ? 'esteticistas' :
                    'ginecólogos'} en ${isCDMX ? 'Ciudad de México' : cityInfo?.cityName || ''}, ${cityInfo?.stateName || ''}`}
        specialty={currentSpecialty}
        city={cityInfo?.cityName || ''}
        state={cityInfo?.stateName || ''}
        totalDoctors={pagination.total || places.length}
        doctors={places}
        canonicalUrl={window.location.href}
      />
      
      {/* Header fijo con breadcrumbs y búsqueda */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumbs items={getBreadcrumbItems()} />

          {/* Barra de búsqueda */}
          <div className="mt-4">
            <input
              type="text"
              placeholder="Buscar por nombre o dirección..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Total de resultados */}
          {!loading && !error && (
            <div className="text-center text-gray-600 mt-4">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {pagination.total} {specialty === 'urologos' ? 'Urólogos' : 
                 specialty === 'pediatras' ? 'Pediatras' : 
                 specialty === 'dermatologos' ? 'Dermatólogos' : 
                 specialty === 'traumatologos' ? 'Traumatólogos' :
                 specialty === 'dentistas' ? 'Dentistas' :
                 specialty === 'otorrinolaringologos' ? 'Otorrinolaringólogos' :
                 specialty === 'nutriologos' ? 'Nutriólogos' :
                 specialty === 'oftalmologos' ? 'Oftalmólogos' :
                 specialty === 'psiquiatras' ? 'Psiquiatras' :
                 specialty === 'cardiologos' ? 'Cardiólogos' :
                 specialty === 'endocrinologos' ? 'Endocrinólogos' :
                 specialty === 'gastroenterologos' ? 'Gastroenterólogos' :
                 specialty === 'cirujanos-plasticos' ? 'Cirujanos Plásticos' :
                 specialty === 'psicologos' ? 'Psicólogos' :
                 specialty === 'podologos' ? 'Podólogos' :
                 specialty === 'acupuntores' ? 'Acupuntores' :
                 specialty === 'clinica de acupuntura' ? 'Acupuntores' :
                 specialty === 'reumatologos' ? 'Reumatologos' :
                 specialty === 'internistas' ? 'Internistas' :
                 specialty === 'geriatras' ? 'Geriatras' :
                 specialty === 'angiologos' ? 'Angiologos' :
                 specialty === 'neurologos' ? 'Neurologos' :
                 specialty === 'homeopatas' ? 'Homeopatas' :
                 specialty === 'alergologos' ? 'Alergologos' :
                 specialty === 'fisioterapeutas' ? 'Fisioterapeutas' :
                 specialty === 'laboratorios-de-analisis-clinicos' ? 'Laboratorios de Análisis Clínicos' :
                 specialty === 'nefrologos' ? 'Nefrologos' :
                 specialty === 'proctologos' ? 'Proctologos' :
                 specialty === 'neumologos' ? 'Neumologos' :
                 specialty === 'medicos-familiares' ? 'Medicos Familiares' :
                 specialty === 'spa-terapeuticos' ? 'Spa Terapéuticos' :
                 specialty === 'farmacias' ? 'Farmacias' :
                 specialty === 'audiologos' ? 'Audiologos' :
                 specialty === 'masajistas' ? 'Masajistas' :
                 specialty === 'tiendas-de-articulos-ortopedicos' ? 'Tiendas de Articulos Ortopédicos' :
                 specialty === 'tiendas-de-sillas-de-ruedas' ? 'Tiendas de Sillas de Ruedas' :
                 specialty === 'opticas' ? 'Opticas' :
                 specialty === 'radiologos' ? 'Radiologos' :
                 specialty === 'optometristas' ? 'Optometristas' :
                 specialty === 'quiropractico' ? 'Quiropracticos' :
                 specialty === 'periodoncistas' ? 'Periodoncistas' :
                 specialty === 'esteticistas' ? 'Esteticistas' :
                 'Ginecólogos'} 
                ({places.filter(place => place.rating?.average >= 4.0).length} altamente calificados) en {isCDMX ? 'Ciudad de México' : cityInfo?.cityName}
              </h1>
              
              <p className="text-gray-600">
                Se encontraron {pagination.total} {specialty === 'urologos' ? 'Urólogos' : 
                                          specialty === 'pediatras' ? 'Pediatras' : 
                                          specialty === 'dermatologos' ? 'Dermatólogos' : 
                                          specialty === 'traumatologos' ? 'Traumatólogos' :
                                          specialty === 'dentistas' ? 'Dentistas' :
                                          specialty === 'otorrinolaringologos' ? 'Otorrinolaringólogos' :
                                          specialty === 'nutriologos' ? 'Nutriólogos' :
                                          specialty === 'oftalmologos' ? 'Oftalmólogos' :
                                          specialty === 'psiquiatras' ? 'Psiquiatras' :
                                          specialty === 'cardiologos' ? 'Cardiólogos' :
                                          specialty === 'endocrinologos' ? 'Endocrinólogos' :
                                          specialty === 'gastroenterologos' ? 'Gastroenterólogos' :
                                          specialty === 'cirujanos-plasticos' ? 'Cirujanos Plásticos' :
                                          specialty === 'psicologos' ? 'Psicólogos' :
                                          specialty === 'podologos' ? 'Podólogos' :
                                          specialty === 'acupuntores' ? 'Acupuntores' :
                                          specialty === 'clinica de acupuntura' ? 'Acupuntores' :
                                          specialty === 'reumatologos' ? 'Reumatologos' :
                                          specialty === 'internistas' ? 'Internistas' :
                                          specialty === 'geriatras' ? 'Geriatras' :
                                          specialty === 'angiologos' ? 'Angiologos' :
                                          specialty === 'neurologos' ? 'Neurologos' :
                                          specialty === 'homeopatas' ? 'Homeopatas' :
                                          specialty === 'alergologos' ? 'Alergologos' :
                                          specialty === 'fisioterapeutas' ? 'Fisioterapeutas' :
                                          specialty === 'laboratorios-de-analisis-clinicos' ? 'Laboratorios de Análisis Clínicos' :
                                          specialty === 'nefrologos' ? 'Nefrologos' :
                                          specialty === 'proctologos' ? 'Proctologos' :
                                          specialty === 'neumologos' ? 'Neumologos' :
                                          specialty === 'medicos-familiares' ? 'Medicos Familiares' :
                                          specialty === 'spa-terapeuticos' ? 'Spa Terapéuticos' :
                                          specialty === 'farmacias' ? 'Farmacias' :
                                          specialty === 'audiologos' ? 'Audiologos' :
                                          specialty === 'masajistas' ? 'Masajistas' :
                                          specialty === 'tiendas-de-articulos-ortopedicos' ? 'Tiendas de Articulos Ortopédicos' :
                                          specialty === 'tiendas-de-sillas-de-ruedas' ? 'Tiendas de Sillas de Ruedas' :
                                          specialty === 'opticas' ? 'Opticas' :
                                          specialty === 'radiologos' ? 'Radiologos' :
                                          specialty === 'optometristas' ? 'Optometristas' :
                                          specialty === 'quiropractico' ? 'Quiropracticos' :
                                          specialty === 'periodoncistas' ? 'Periodoncistas' :
                                          specialty === 'esteticistas' ? 'Esteticistas' :
                                          'Ginecólogos'}
                {isCDMX ? ' en toda la Ciudad de México' : ''}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Contenido principal con scroll */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div>
          {loading ? (
            <div className="text-center py-12">Cargando...</div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">{error}</div>
          ) : places.length === 0 ? (
            <div className="text-center py-12">
              No se encontraron resultados para tu búsqueda.
            </div>
          ) : (
            <>
              <div className="doctors-list" itemScope itemType="https://schema.org/ItemList">
                {places.map((doctor, index) => (
                  <div key={doctor._id || doctor.id} className="mb-6">
                    <DoctorCard 
                      doctor={doctor} 
                      index={index} 
                      key={doctor._id || doctor.id}
                    />
                  </div>
                ))}
              </div>

              {pagination.totalPages > 1 && (
                <div className="mt-8">
                  <Pagination 
                    currentPage={page}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaceList;